import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import "./themeFonts.css";
import { isThemeId, themeDefinition, type ThemeId } from "./themeCatalog";
import { applyThemeToDocument, availableThemeDefinitions, parseThemeAvailability, resolveThemeId, type ThemeAvailability } from "./themeRuntime";
import { frozenThemeTokens } from "./themeTokens.generated";
import { readEmbedRequest } from "../onboarding/embedMode";
import { AppThemeContext, type AppThemeContextValue,type UserThemePersistence } from "./themeContext";

export type { AppThemeContextValue } from "./themeContext";

function readStoredThemeId(storageKey: string): ThemeId | null {
  try {
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem(storageKey) : null;
    return isThemeId(stored) ? stored : null;
  } catch {
    return null;
  }
}

function writeStoredThemeId(storageKey: string, id: ThemeId | null) {
  try {
    if (id) localStorage.setItem(storageKey, id);
    else localStorage.removeItem(storageKey);
  } catch {
    // Ohne Speicher gilt die Wahl bis zum Neuladen.
  }
}

export type ThemeProviderProps = {
  children: React.ReactNode;
  /** Standard des Produkts, solange keine Freigabe geladen ist. */
  productDefaultThemeId: ThemeId;
  /** Gerätelokale Ablage der Benutzerwahl, z. B. je Konto. */
  storageKey: string;
  /** Liest einmalig eine frühere Auswahl, falls unter `storageKey` noch nichts liegt. */
  readLegacyThemeId?: () => ThemeId | null;
  /** Serverseitige Speicherung der Benutzerwahl, falls das Produkt sie hat. */
  onUserThemeChange?: UserThemePersistence;
  /** Erzwingt ein Theme (z. B. öffentliche Widgets); Auswahl und Vorgaben bleiben erhalten. */
  forcedThemeId?: ThemeId | null;
};

/**
 * Gemeinsame Theme-Laufzeit aller Produkte: wählt das wirksame Theme aus Benutzerwahl, Mandanten-
 * und App-Standard, setzt Merkmale und `--mq-theme-*`-Variablen am Dokument und liefert die Antd-Konfiguration.
 */
export function ThemeProvider({ children, productDefaultThemeId, storageKey, readLegacyThemeId, onUserThemeChange, forcedThemeId }: ThemeProviderProps) {
  const [stored, setStored] = useState<{ key: string; id: ThemeId | null }>(() => ({ key: storageKey, id: readStoredThemeId(storageKey) ?? readLegacyThemeId?.() ?? null }));
  const [availability, setAvailability] = useState<ThemeAvailability | null>(null);
  const [previewThemeId, setPreviewThemeId] = useState<ThemeId | null>(null);
  const [themeDrawer, setThemeDrawer] = useState<AppThemeContextValue["themeDrawer"]>(null);
  // Eingebettete öffentliche Seiten tragen das Theme der einbettenden Seite in der Adresse; nichts davon wird gespeichert.
  const [embedThemeId] = useState(() => readEmbedRequest().themeId);

  // Wechselt die Ablage (z. B. anderes Konto), gilt deren Wahl ab dem nächsten Render.
  const userThemeId = stored.key === storageKey ? stored.id : readStoredThemeId(storageKey) ?? readLegacyThemeId?.() ?? null;
  if (stored.key !== storageKey) {
    setStored({ key: storageKey, id: userThemeId });
    setPreviewThemeId(null);
    setThemeDrawer(null);
  }

  const resolvedId = resolveThemeId({ userThemeId, availability, productDefaultThemeId });
  const themeId = forcedThemeId ?? embedThemeId ?? themeDrawer?.selectedThemeId ?? previewThemeId ?? resolvedId;
  const theme = themeDefinition(themeId);
  const openThemeDrawer = useCallback((tr: (key: string) => string) => {
    setThemeDrawer(current => current ?? { initialThemeId: resolvedId, selectedThemeId: themeId, tr });
  }, [resolvedId, themeId]);
  const setThemeDrawerPreview = useCallback((id: ThemeId | null) => {
    setThemeDrawer(current => current ? { ...current, selectedThemeId: id ?? current.initialThemeId } : current);
  }, []);
  const closeThemeDrawer = useCallback(() => {
    setPreviewThemeId(null);
    setThemeDrawer(null);
  }, []);

  useLayoutEffect(() => {
    applyThemeToDocument(theme);
  }, [theme]);

  const persistRef = useRef<UserThemePersistence | null>(null);
  const ownerRef=useRef(storageKey);
  const mounted=useRef(true);
  useLayoutEffect(()=>{ownerRef.current=storageKey;mounted.current=true;return()=>{mounted.current=false;};},[storageKey]);
  const registerUserThemePersistence = useCallback((persist: UserThemePersistence | null) => {
    persistRef.current = persist;
  }, []);

  const setThemeId = useCallback((id: ThemeId):Promise<boolean> => {
    const persist=persistRef.current;
    const adopt=(confirmed:ThemeId)=>{
      if(!mounted.current || ownerRef.current!==storageKey || persistRef.current!==persist)return false;
      writeStoredThemeId(storageKey,confirmed);
      setStored({key:storageKey,id:confirmed});setPreviewThemeId(null);
      return true;
    };
    // Local-only products keep their synchronous theme selection.
    if(!persist && !onUserThemeChange)return Promise.resolve(adopt(id));
    return (async()=>{
      const direct=await onUserThemeChange?.(id);
      if(direct===false)return false;
      const saved=await persist?.(id);
      if(saved===false)return false;
      return adopt(isThemeId(saved)?saved:isThemeId(direct)?direct:id);
    })();
  }, [onUserThemeChange, storageKey]);

  const adoptUserThemeId = useCallback((id: ThemeId | null) => {
    writeStoredThemeId(storageKey, id);
    setStored({ key: storageKey, id });
  }, [storageKey]);

  const loaderRef = useRef<(() => Promise<unknown>) | null>(null);
  const registerAvailabilityLoader = useCallback((load: (() => Promise<unknown>) | null) => {
    loaderRef.current = load;
  }, []);
  const reloadAvailability = useCallback(async (options?:{strict?:boolean}) => {
    const load = loaderRef.current;
    if (!load) return;
    try {
      const next = parseThemeAvailability(await load());
      if(options?.strict&&!next)throw new Error('Theme.loadFailed');
      if (loaderRef.current === load) setAvailability(next);
      else if(options?.strict)throw new Error('Theme.loadFailed');
    } catch (error) {
      if(options?.strict)throw error;
      // Ohne Vorgaben vom Server gilt der Produktstandard; die Oberfläche bleibt bedienbar.
    }
  }, []);

  const value = useMemo<AppThemeContextValue>(() => ({
    themeDrawer,
    openThemeDrawer,
    closeThemeDrawer,
    setThemeDrawerPreview,
    themeId,
    theme,
    token: frozenThemeTokens[theme.id],
    fieldVariant: theme.fieldVariant,
    availableThemes: availableThemeDefinitions(availability),
    availability,
    userThemeId,
    setThemeId,
    adoptUserThemeId,
    setAvailability,
    reloadAvailability,
    registerAvailabilityLoader,
    registerUserThemePersistence,
    previewThemeId: themeDrawer ? themeDrawer.selectedThemeId === themeDrawer.initialThemeId ? null : themeDrawer.selectedThemeId : previewThemeId,
    setPreviewThemeId,
  }), [themeDrawer, openThemeDrawer, closeThemeDrawer, setThemeDrawerPreview, adoptUserThemeId, availability, previewThemeId, registerAvailabilityLoader, registerUserThemePersistence, reloadAvailability, setThemeId, theme, themeId, userThemeId]);

  return <AppThemeContext.Provider value={value}>{children}</AppThemeContext.Provider>;
}

